import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  FileText, 
  User, 
  Calendar, 
  DollarSign, 
  Package, 
  Building, 
  FileCheck,
  RefreshCw,
  Eye,
  Edit,
  Download,
  Upload,
  BarChart3,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getChargebackDetail, 
  validateChargeback, 
  approveChargeback, 
  rejectChargeback,
  getChargebackValidationHistory,
  exportChargebackReport
} from '../../config/api';
import RejectionReasonSelector from '../../components/RejectionReasonSelector';

const ChargebackValidationInterface = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [validationResults, setValidationResults] = useState(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  // Backend requires a catalog RejectionReason id; the free-text field is optional detail.
  const [rejectionReasonId, setRejectionReasonId] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch chargeback details
  const { data: chargeback, isLoading, error, refetch } = useQuery({
    queryKey: ['chargeback-detail', id],
    queryFn: () => getChargebackDetail(id),
    enabled: !!id && !!user
  });

  // Fetch validation history
  const { data: validationHistory } = useQuery({
    queryKey: ['chargeback-validation-history', id],
    queryFn: () => getChargebackValidationHistory(id),
    enabled: !!id && !!user
  });

  // Mutations
  const validateMutation = useMutation({
    mutationFn: validateChargeback,
    onSuccess: (data) => {
      setValidationResults(data);
      setShowValidationDetails(true);
      toast.success('Validation completed successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback-detail', id] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Validation failed');
    }
  });

  const approveMutation = useMutation({
    mutationFn: approveChargeback,
    onSuccess: () => {
      toast.success('Chargeback approved successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback-detail', id] });
      setShowApprovalModal(false);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve chargeback');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: rejectChargeback,
    onSuccess: () => {
      toast.success('Chargeback rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback-detail', id] });
      setShowRejectionModal(false);
      setRejectionReason('');
      setRejectionReasonId('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject chargeback');
    }
  });

  const exportMutation = useMutation({
    mutationFn: exportChargebackReport,
    onSuccess: (data) => {
      // Download the exported file
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chargeback-report-${chargeback?.claimNumber || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report exported successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Export failed');
    }
  });

  // Handlers
  const handleValidate = () => {
    validateMutation.mutate({
      chargebackId: id,
      validationType: 'COMPREHENSIVE_VALIDATION'
    });
  };

  const handleApprove = () => {
    if (!approvalNotes.trim()) {
      toast.error('Please provide approval notes');
      return;
    }

    approveMutation.mutate({
      chargebackId: id,
      notes: approvalNotes,
      approvedBy: user.id
    });
  };

  const handleReject = () => {
    if (!rejectionReasonId) {
      toast.error('Please select a rejection reason');
      return;
    }

    rejectMutation.mutate({
      chargebackId: id,
      rejectionReasonId,
      rejectedBy: user.id,
      rejectionDetails: {
        additionalNotes: rejectionReason || '',
        rejectedAt: new Date().toISOString()
      }
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'REJECTED': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'PENDING': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'PROCESSING': return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'NEEDS_REVIEW': return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'PROCESSING': return 'bg-blue-100 text-blue-800';
      case 'NEEDS_REVIEW': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getValidationStatusIcon = (isValid) => {
    return isValid ? 
      <CheckCircle className="h-4 w-4 text-green-500" /> : 
      <XCircle className="h-4 w-4 text-red-500" />;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading chargeback</h3>
        <p className="mt-1 text-sm text-gray-500">
          {error.response?.data?.message || 'Failed to load chargeback details. Please try again.'}
        </p>
        <button
          onClick={() => refetch()}
          title="Try again"
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            title="Go back"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chargeback Validation</h1>
            <p className="text-gray-600">Validate and approve chargeback claims</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => refetch()}
            title="Refresh"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => exportMutation.mutate({ chargebackId: id })}
            disabled={exportMutation.isPending}
            title="Export report"
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Chargeback Summary */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Chargeback Summary</h2>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(chargeback?.status)}`}>
            {getStatusIcon(chargeback?.status)}
            <span className="ml-2">{chargeback?.status}</span>
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <FileText className="h-8 w-8 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-600">Claim Number</p>
              <p className="text-lg font-semibold text-gray-900">{chargeback?.claimNumber}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <DollarSign className="h-8 w-8 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-lg font-semibold text-gray-900">
                ${chargeback?.totalAmount?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Calendar className="h-8 w-8 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-600">Claim Date</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(chargeback?.claimDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: Eye },
              { id: 'validation', name: 'Validation', icon: FileCheck },
              { id: 'line-items', name: 'Line Items', icon: Package },
              { id: 'history', name: 'History', icon: BarChart3 }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                title={tab.name}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Building className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Customer</p>
                        <p className="text-sm text-gray-900">{chargeback?.customer?.name || 'Unknown'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Contact</p>
                        <p className="text-sm text-gray-900">{chargeback?.customer?.primaryContact || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Contract</p>
                        <p className="text-sm text-gray-900">{chargeback?.contract?.number || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Effective Period</p>
                        <p className="text-sm text-gray-900">
                          {chargeback?.contract?.effectiveFrom && chargeback?.contract?.effectiveTo
                            ? `${new Date(chargeback.contract.effectiveFrom).toLocaleDateString()} - ${new Date(chargeback.contract.effectiveTo).toLocaleDateString()}`
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleValidate}
                  disabled={validateMutation.isPending}
                  title="Run validation"
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <FileCheck className="h-4 w-4 mr-2" />
                  {validateMutation.isPending ? 'Validating...' : 'Run Validation'}
                </button>
                
                {chargeback?.status === 'PENDING' && (
                  <>
                    <button
                      onClick={() => setShowApprovalModal(true)}
                      title="Approve chargeback"
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => setShowRejectionModal(true)}
                      title="Reject chargeback"
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Validation Tab */}
          {activeTab === 'validation' && (
            <div className="space-y-6">
              {validationResults ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Validation Results</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      validationResults.overallValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {getValidationStatusIcon(validationResults.overallValid)}
                      <span className="ml-2">
                        {validationResults.overallValid ? 'Valid' : 'Invalid'}
                      </span>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Validation Checks</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Price Validation</span>
                          {getValidationStatusIcon(validationResults.isPriceValid)}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Quantity Validation</span>
                          {getValidationStatusIcon(validationResults.isQuantityValid)}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Customer Validation</span>
                          {getValidationStatusIcon(validationResults.isCustomerValid)}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Product Validation</span>
                          {getValidationStatusIcon(validationResults.isProductValid)}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Contract Validation</span>
                          {getValidationStatusIcon(validationResults.isContractValid)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Price Analysis</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Expected Price</span>
                          <span className="text-sm font-medium text-gray-900">
                            ${validationResults.expectedPrice != null ? Number(validationResults.expectedPrice).toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Actual Price</span>
                          <span className="text-sm font-medium text-gray-900">
                            ${validationResults.actualPrice != null ? Number(validationResults.actualPrice).toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Variance</span>
                          <span className={`text-sm font-medium ${
                            validationResults.priceVariance > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {validationResults.priceVariance > 0 ? (
                              <TrendingUp className="h-4 w-4 inline mr-1" />
                            ) : (
                              <TrendingDown className="h-4 w-4 inline mr-1" />
                            )}
                            {validationResults.priceVariance != null ? Number(validationResults.priceVariance).toFixed(2) : '0'}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {validationResults.issues && validationResults.issues.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Issues Found</h4>
                      <div className="space-y-2">
                        {validationResults.issues.map((issue, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
                            <span className="text-sm text-red-600">{issue}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {validationResults.recommendations && validationResults.recommendations.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                      <div className="space-y-2">
                        {validationResults.recommendations.map((recommendation, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                            <span className="text-sm text-green-600">{recommendation}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileCheck className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No validation results</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Run validation to see detailed results.
                  </p>
                  <button
                    onClick={handleValidate}
                    disabled={validateMutation.isPending}
                    title="Run validation"
                    className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Run Validation
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Line Items Tab */}
          {activeTab === 'line-items' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
                <button
                  onClick={() => navigate(`/app/chargebacks/${id}/line-items`)}
                  title="Edit line items"
                  className="flex items-center px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 rounded-md hover:bg-primary-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Line Items
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sales Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contract Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {chargeback?.lines?.map((line) => (
                      <tr key={line.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Package className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {line.product?.name || 'Unknown Product'}
                              </div>
                              <div className="text-sm text-gray-500">
                                NDC: {line.product?.ndc11 || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {line.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${Number(line.salesPrice || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {line.contractPrice != null ? `$${Number(line.contractPrice).toFixed(2)}` : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(line.validationStatus)}`}>
                            {getStatusIcon(line.validationStatus)}
                            <span className="ml-1">{line.validationStatus}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Validation History</h3>
              
              {validationHistory?.length > 0 ? (
                <div className="space-y-4">
                  {validationHistory.map((entry, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            entry.overallValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {getValidationStatusIcon(entry.overallValid)}
                            <span className="ml-1">
                              {entry.overallValid ? 'Valid' : 'Invalid'}
                            </span>
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(entry.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          by {entry.validatedBy || 'System'}
                        </span>
                      </div>
                      
                      {entry.issues && entry.issues.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700">Issues:</p>
                          <ul className="mt-1 text-sm text-gray-600 list-disc list-inside">
                            {entry.issues.map((issue, issueIndex) => (
                              <li key={issueIndex}>{issue}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No validation history</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Validation history will appear here after running validations.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Approve Chargeback</h3>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  title="Close"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approval Notes
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Enter approval notes..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  title="Cancel"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  title="Approve chargeback"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {approveMutation.isPending ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Reject Chargeback</h3>
                <button
                  onClick={() => setShowRejectionModal(false)}
                  title="Close"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <RejectionReasonSelector
                  selectedReason={rejectionReasonId}
                  onReasonSelect={setRejectionReasonId}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Add any additional detail..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  title="Cancel"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                  title="Reject chargeback"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargebackValidationInterface;
